/**
 * A oportunidade aberta de dentro do Cliente 360 (28/09/2026).
 *
 * O MESMO card grande do funil — resumo, notas, conversas, documentos e
 * histórico —, buscado por id (`GET /v1/opportunities/{id}`) em vez de sair
 * do quadro. Duas telas com o mesmo negócio e dois desenhos dele seriam duas
 * verdades para a mesma pergunta.
 *
 * O que fica de fora daqui é EDITAR os dados: a edição mora no funil, com a
 * checagem de etapa e a regra de perda. O card aberto no 360 lê, anota e leva
 * à conversa; para mudar valor ou etapa, o funil.
 */

import { useCallback, useState, type ReactNode } from 'react';
import { chaves } from '@/shared/api/chaves';
import { useApi, useOportunidade } from '@/shared/api/consultas';
import { useInvalidar } from '@/shared/api/provedor';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { Botao, Dialogo } from '@/shared/ui';
import { DialogoDaOportunidade } from '@/widgets/detalhe-da-oportunidade';
import { nomeDaEtapa } from '@/entities/comercial/etapa';

export function OportunidadeNo360({
  oportunidadeId,
  aoAbrirConversa,
  aoFechar,
}: {
  oportunidadeId: string;
  aoAbrirConversa: (conversaId: string) => void;
  aoFechar: () => void;
}): ReactNode {
  const api = useApi();
  const invalidar = useInvalidar();
  const consulta = useOportunidade(oportunidadeId);
  const [anotando, definirAnotando] = useState(false);
  const [erroDaNota, definirErroDaNota] = useState<string | null>(null);

  const anotar = useCallback(
    (texto: string) => {
      if (!api) return;
      definirAnotando(true);
      definirErroDaNota(null);
      void api
        .criarNotaDaOportunidade({
          caminho: { id: oportunidadeId },
          corpo: { texto },
          // Por tentativa: a nota não se apaga, e a duplicata do segundo
          // clique ficaria para sempre.
          idempotencyKey:
            globalThis.crypto?.randomUUID?.() ?? `nota-${Date.now()}-${Math.random()}`,
        })
        .then(() => invalidar([chaves.notasDaOportunidade(oportunidadeId)]))
        .catch((erro: unknown) =>
          definirErroDaNota(
            recadoDeErro(erro, { generica: 'Não foi possível anotar. Tente de novo.' }),
          ),
        )
        .finally(() => definirAnotando(false));
    },
    [api, invalidar, oportunidadeId],
  );

  if (consulta.data) {
    return (
      <DialogoDaOportunidade
        card={consulta.data.card}
        etapas={consulta.data.etapas.map((e) => ({ chave: e.chave, nome: nomeDaEtapa(e.nome) }))}
        anotando={anotando}
        erroDaNota={erroDaNota}
        aoAnotar={anotar}
        aoAbrirConversa={aoAbrirConversa}
        aoFechar={aoFechar}
      />
    );
  }

  // Carregando, ou sem conseguir: a mesma janela, para o clique ter resposta.
  return (
    <Dialogo
      titulo="Oportunidade"
      aoFechar={aoFechar}
      botoes={<Botao onClick={aoFechar}>Fechar</Botao>}
    >
      {consulta.isError ? (
        <p className="dialogo__erro" role="alert">
          {recadoDeErro(consulta.error, {
            generica: 'Não foi possível abrir esta oportunidade. Tente de novo.',
          })}
        </p>
      ) : (
        <p>Abrindo a oportunidade…</p>
      )}
    </Dialogo>
  );
}
